import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Form,
  InputGroup,
  Alert,
  Pagination,
  Dropdown,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  People,
  Search,
  Eye,
  Prescription,
  Telephone,
  Envelope,
  Calendar,
  InfoCircle,
  GraphUp,
  ArrowUp,
  ArrowDown,
  Download,
  Printer,
  PersonCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
} from 'react-bootstrap-icons';

// Données fictives étendues (ajout de photos)
const allPatients = [
  { id: 'CNAMGS-123456', name: 'Jean Dupont', birthDate: '15/05/1985', lastVisit: '2025-03-12', phone: '+241 01 23 45 67', email: 'jean.dupont@email.ga', prescriptionsCount: 5, photo: 'https://randomuser.me/api/portraits/men/1.jpg' },
  { id: 'CNAMGS-789012', name: 'Marie Nzogho', birthDate: '23/09/1990', lastVisit: '2025-03-10', phone: '+241 01 23 45 68', email: 'marie.nzogho@email.ga', prescriptionsCount: 3, photo: 'https://randomuser.me/api/portraits/women/2.jpg' },
  { id: 'CNAMGS-345678', name: 'Pierre Mba', birthDate: '10/12/1978', lastVisit: '2025-03-05', phone: '+241 01 23 45 69', email: 'pierre.mba@email.ga', prescriptionsCount: 2, photo: 'https://randomuser.me/api/portraits/men/3.jpg' },
  { id: 'CNAMGS-111222', name: 'Sophie Nguema', birthDate: '02/07/1995', lastVisit: '2025-03-15', phone: '+241 01 23 45 70', email: 'sophie.nguema@email.ga', prescriptionsCount: 1, photo: 'https://randomuser.me/api/portraits/women/4.jpg' },
  { id: 'CNAMGS-333444', name: 'Paul Obiang', birthDate: '18/11/1982', lastVisit: '2025-03-08', phone: '+241 01 23 45 71', email: 'paul.obiang@email.ga', prescriptionsCount: 4, photo: 'https://randomuser.me/api/portraits/men/5.jpg' },
  { id: 'CNAMGS-555666', name: 'Claire Moussavou', birthDate: '30/01/1989', lastVisit: '2025-03-14', phone: '+241 01 23 45 72', email: 'claire.moussavou@email.ga', prescriptionsCount: 6, photo: 'https://randomuser.me/api/portraits/women/6.jpg' },
  { id: 'CNAMGS-777888', name: 'David Ngoma', birthDate: '05/04/1975', lastVisit: '2025-03-02', phone: '+241 01 23 45 73', email: 'david.ngoma@email.ga', prescriptionsCount: 2, photo: 'https://randomuser.me/api/portraits/men/7.jpg' },
  { id: 'CNAMGS-999000', name: 'Anne Mintogo', birthDate: '22/12/1992', lastVisit: '2025-03-09', phone: '+241 01 23 45 74', email: 'anne.mintogo@email.ga', prescriptionsCount: 3, photo: 'https://randomuser.me/api/portraits/women/8.jpg' },
];

const MyPatients = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [exportLoading, setExportLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    const newSet = new Set(expandedRows);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setExpandedRows(newSet);
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  // Filtrer et trier
  const filteredPatients = useMemo(() => {
    let filtered = allPatients.filter(p =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.phone.includes(searchTerm)
    );
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === 'name') {
        aVal = a.name;
        bVal = b.name;
      } else if (sortField === 'lastVisit') {
        aVal = new Date(a.lastVisit);
        bVal = new Date(b.lastVisit);
      }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [searchTerm, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / itemsPerPage);
  const paginatedPatients = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredPatients.slice(start, start + itemsPerPage);
  }, [filteredPatients, currentPage, itemsPerPage]);

  // Statistiques
  const totalPatients = filteredPatients.length;
  const avgAge = Math.round(filteredPatients.reduce((sum, p) => sum + calculateAge(p.birthDate), 0) / totalPatients) || 0;
  const totalPrescriptions = filteredPatients.reduce((sum, p) => sum + p.prescriptionsCount, 0);
  const mostRecentDate = filteredPatients.length ? new Date(Math.max(...filteredPatients.map(p => new Date(p.lastVisit)))) : null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const renderSortIcon = (field) => sortField === field ? (sortDirection === 'asc' ? <ArrowUp size={12} className="ms-1" /> : <ArrowDown size={12} className="ms-1" />) : null;

  // Pagination items
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
        {i}
      </Pagination.Item>
    );
  }

  // Export/Print handlers (simulation)
  const handleExport = () => {
    setExportLoading(true);
    setTimeout(() => { alert('Export PDF (simulation)'); setExportLoading(false); }, 800);
  };
  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => { window.print(); setPrinting(false); }, 500);
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête avec actions */}
        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 className="main-title mb-0 d-flex align-items-center gap-2">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                <People size={24} className="text-primary" />
              </div>
              Mes patients
            </h4>
            <p className="text-muted mb-0 mt-1">
              Liste des patients que vous avez consultés
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={handleExport} disabled={exportLoading}>
              <Download size={14} className="me-1" /> {exportLoading ? 'Export...' : 'Exporter'}
            </Button>
            <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={handlePrint} disabled={printing}>
              <Printer size={14} className="me-1" /> {printing ? 'Impression...' : 'Imprimer'}
            </Button>
          </div>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          <Col md={4} xs={6}>
            <Card className="border-0 shadow-sm stats-card bg-white h-100">
              <div className="card-gradient bg-primary"></div>
              <Card.Body className="p-3 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-1 small">Total patients</h6>
                    <h3 className="mb-0 fw-bold">{totalPatients}</h3>
                  </div>
                  <div className="icon-container bg-primary-subtle">
                    <People size={28} className="text-primary" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4} xs={6}>
            <Card className="border-0 shadow-sm stats-card bg-white h-100">
              <div className="card-gradient bg-success"></div>
              <Card.Body className="p-3 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-1 small">Âge moyen</h6>
                    <h3 className="mb-0 fw-bold">{avgAge} ans</h3>
                  </div>
                  <div className="icon-container bg-success-subtle">
                    <GraphUp size={28} className="text-success" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm stats-card bg-white h-100">
              <div className="card-gradient bg-warning"></div>
              <Card.Body className="p-3 d-flex flex-column">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="text-muted mb-1 small">Ordonnances totales</h6>
                    <h3 className="mb-0 fw-bold">{totalPrescriptions}</h3>
                  </div>
                  <div className="icon-container bg-warning-subtle">
                    <Prescription size={28} className="text-warning" />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Barre de recherche et filtres */}
        <Card className="border-0 shadow-sm mb-4 bg-white" style={{ borderRadius: '16px' }}>
          <Card.Body className="p-4">
            <Row className="align-items-center g-3">
              <Col md={6} xs={6}>
                <InputGroup>
                  <InputGroup.Text className="bg-light border-0">
                    <Search size={18} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher par nom, numéro d’assuré ou téléphone..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="border-0 bg-light"
                  />
                </InputGroup>
              </Col>
              <Col md={3} xs={6}>
                <Form.Select
                  value={itemsPerPage}
                  onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="border-0 bg-light"
                >
                  <option value={5}>5 par page</option>
                  <option value={10}>10 par page</option>
                  <option value={20}>20 par page</option>
                </Form.Select>
              </Col>
              <Col md={3}>
                <div className="text-muted small text-md-end">
                  {filteredPatients.length} patient(s) trouvé(s)
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des patients avec adaptation mobile */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
          <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
            <h5 className="fw-semibold mb-0">Liste des patients</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle table-striped">
                <thead className="bg-warning-opacity">
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>
                      Patient {renderSortIcon('name')}
                    </th>
                    <th className="d-none d-sm-table-cell">N° d'assuré</th>
                    <th className="d-none d-sm-table-cell" style={{ cursor: 'pointer' }} onClick={() => handleSort('lastVisit')}>
                      Dernière visite {renderSortIcon('lastVisit')}
                    </th>
                    <th className="d-none d-sm-table-cell">Contact</th>
                    <th className="d-none d-sm-table-cell">Ordonnances</th>
                    <th>Actions</th>
                    <th className="d-sm-none text-center">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPatients.length > 0 ? (
                    paginatedPatients.map(patient => {
                      const isExpanded = expandedRows.has(patient.id);
                      return (
                        <React.Fragment key={patient.id}>
                          {/* Main row */}
                          <tr className="patient-row">
                            <td data-label="Patient">
                              <div className="d-flex align-items-center gap-3">
                                <img src={patient.photo} alt={patient.name} className="rounded-circle" style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
                                <div>
                                  <div className="fw-semibold">{patient.name}</div>
                                  <div className="small text-muted">{calculateAge(patient.birthDate)} ans</div>
                                </div>
                              </div>
                            </td>
                            <td data-label="N° d'assuré" className="d-none d-sm-table-cell text-nowrap">{patient.id}</td>
                            <td data-label="Dernière visite" className="d-none d-sm-table-cell text-nowrap">{formatDate(patient.lastVisit)}</td>
                            <td data-label="Contact" className="d-none d-sm-table-cell">
                              <div className="d-flex flex-column">
                                <div className="small"><Telephone size={12} className="me-1" /> {patient.phone}</div>
                                <div className="small"><Envelope size={12} className="me-1" /> {patient.email}</div>
                              </div>
                            </td>
                            <td data-label="Ordonnances" className="d-none d-sm-table-cell">
                              <Badge bg="warning" className="rounded-pill px-2 py-1">{patient.prescriptionsCount}</Badge>
                            </td>
                            <td data-label="Actions">
                              <div className="d-flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => navigate(`/doctor/patient-record/${patient.id}`)}
                                  title="Voir dossier"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="success"
                                  className="rounded-circle p-2 d-flex align-items-center justify-content-center"
                                  style={{ width: '32px', height: '32px' }}
                                  onClick={() => navigate(`/doctor/prescribe/${patient.id}`)}
                                  title="Prescrire"
                                >
                                  <Prescription size={14} />
                                </Button>
                              </div>
                            </td>
                            <td className="d-sm-none text-center" style={{ verticalAlign: 'middle' }}>
                              <Button
                                variant="link"
                                onClick={() => toggleRow(patient.id)}
                                className="p-0 text-secondary"
                                style={{ textDecoration: 'none' }}
                              >
                                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                              </Button>
                            </td>
                           </tr>
                          {/* Expanded row - visible only on mobile */}
                          {isExpanded && (
                            <tr className="d-sm-none detail-row">
                              <td colSpan="7" className="p-3 bg-light">
                                <div className="row g-2">
                                  <div className="col-12">
                                    <strong>N° d'assuré :</strong> {patient.id}
                                  </div>
                                  <div className="col-12">
                                    <strong>Dernière visite :</strong> {formatDate(patient.lastVisit)}
                                  </div>
                                  <div className="col-12">
                                    <strong>Contact :</strong>
                                    <div className="mt-1">
                                      <div><Telephone size={12} className="me-1" /> {patient.phone}</div>
                                      <div><Envelope size={12} className="me-1" /> {patient.email}</div>
                                    </div>
                                  </div>
                                  <div className="col-12">
                                    <strong>Ordonnances :</strong> <Badge bg="warning" className="rounded-pill px-2 py-1">{patient.prescriptionsCount}</Badge>
                                  </div>
                                  <div className="col-12">
                                    <strong>Date de naissance :</strong> {patient.birthDate}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-5">
                        <People size={48} className="text-muted mb-3" />
                        <p className="text-muted">Aucun patient trouvé</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div className="text-muted small">
                  Affichage de {(currentPage-1)*itemsPerPage+1} à {Math.min(currentPage*itemsPerPage, filteredPatients.length)} sur {filteredPatients.length} patients
                </div>
                <Pagination className="mb-0">
                  <Pagination.Prev
                    onClick={() => setCurrentPage(p => Math.max(1, p-1))}
                    disabled={currentPage === 1}
                  />
                  {paginationItems}
                  <Pagination.Next
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Information */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Gestion des patients</strong>
                  <span className="small">
                    Cliquez sur l’icône œil pour consulter le dossier complet, ou sur l’icône prescription pour créer une nouvelle ordonnance.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .icon-container {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .patient-row {
            transition: background-color 0.2s;
          }
          .patient-row:hover {
            background-color: #f8f9fa;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
          /* Responsive table adjustments */
          @media (max-width: 768px) {
            .table td, .table th {
              padding: 0.5rem;
            }
            .table td[data-label] {
              display: table-cell;
            }
            .detail-row td {
              padding: 1rem;
            }
            .btn-sm {
              padding: 0.25rem 0.5rem;
              font-size: 0.75rem;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default MyPatients;